import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, FileText, Filter, Video, Share2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { displayTime, fmtDate, todayISO } from "@/lib/format";
import VideoCallDialog, { meetingLinkFor } from "@/components/VideoCallDialog";
import { useAuth } from "@/lib/auth";

const STATUSES = ["all", "scheduled", "checked_in", "in_consult", "completed", "cancelled", "no_show"];

export default function Appointments() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [date, setDate] = useState(todayISO());
  const [status, setStatus] = useState("all");
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("all");
  const [items, setItems] = useState([]);
  const [callAppt, setCallAppt] = useState(null);
  const [openCall, setOpenCall] = useState(false);

  const load = async () => {
    const q = [];
    if (date) q.push(`date=${date}`);
    if (status !== "all") q.push(`status=${status}`);
    if (facilityId !== "all") q.push(`facility_id=${facilityId}`);
    const { data } = await api.get(`/appointments${q.length ? "?" + q.join("&") : ""}`);
    setItems(data);
  };

  useEffect(() => {
    api.get("/facilities").then(({ data }) => setFacilities((data.items || []).filter((f) => f.active))).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [date, status, facilityId]);

  const setStatusFor = async (id, s) => {
    try { await api.patch(`/appointments/${id}/status`, { status: s }); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const shareLink = async (appt) => {
    const link = meetingLinkFor(appt);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Meeting link copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  const startCall = (appt) => { setCallAppt(appt); setOpenCall(true); };

  return (
    <Layout>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="field-label mb-2">Schedule</div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Appointments</h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {facilities.length > 1 && (
            <select value={facilityId} onChange={(e) => setFacilityId(e.target.value)} className="input-clean" data-testid="appts-facility-filter">
              <option value="all">All facilities</option>
              {facilities.map((f) => <option key={f.id} value={f.id}>{f.name}{f.is_primary ? " · Primary" : ""}</option>)}
            </select>
          )}
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-clean" data-testid="appts-date-filter" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-clean" data-testid="appts-status-filter">
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </div>
      </div>

      <div className="card-soft p-6 md:p-8">
        {items.length === 0 ? (
          <div className="py-16 text-center text-[#8A9992] text-sm">No appointments for {fmtDate(date)}.</div>
        ) : (
          <div className="space-y-2">
            {items.map((a) => (
              <div key={a.id} className="rounded-2xl px-3 sm:px-4 py-3 sm:py-4 row-hover flex flex-wrap md:flex-nowrap items-center gap-2 sm:gap-3" data-testid={`appts-item-${a.id}`}>
                {/* Row 1 on mobile: time chip + patient meta + video/share */}
                <div className="w-14 sm:w-16 flex-none rounded-2xl bg-[#FCE7D9] text-[#5C3A21] flex flex-col items-center justify-center py-2">
                  <div className="text-[10px] tracking-widest uppercase">TIME</div>
                  <div className="text-sm font-semibold whitespace-nowrap">{displayTime(a.time)}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1F2A24] text-sm sm:text-base inline-flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    <span className="truncate">{a.patient_name}</span>
                    {(a.additional_doctor_ids || []).length > 0 && (
                      <span
                        title={
                          a.role_in_appt === "invited"
                            ? `Host: ${a.doctor_name || "colleague"}`
                            : (a.additional_doctor_names || []).join(", ") || "Multi-doctor call"
                        }
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#E6F0EC] text-[#0A4C36] uppercase tracking-widest"
                        data-testid={`appts-multi-${a.id}`}
                      >
                        +{a.additional_doctor_ids.length} DR{a.additional_doctor_ids.length > 1 ? "s" : ""}
                      </span>
                    )}
                    {a.role_in_appt === "invited" && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#FCE7D9] text-[#5C3A21] uppercase tracking-widest">
                        <span className="sm:hidden">Invited</span>
                        <span className="hidden sm:inline">Invited by {a.doctor_name || "colleague"}</span>
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] sm:text-xs text-[#8A9992] truncate">{fmtDate(a.date)} · {a.mode} · {a.status}</div>
                </div>
                <button onClick={() => startCall(a)} title="Start video call" className="flex-none w-9 h-9 rounded-full bg-[#E6F0EC] hover:bg-[#D3E6DD] text-[#0A4C36] flex items-center justify-center" data-testid={`appts-video-${a.id}`}>
                  <Video size={16} />
                </button>
                <button onClick={() => shareLink(a)} title="Share meeting link" className="flex-none w-9 h-9 rounded-full hover:bg-[#F3F1EC] text-[#47554D] flex items-center justify-center" data-testid={`appts-share-${a.id}`}>
                  <Share2 size={16} />
                </button>

                {/* Row 2 on mobile: status + patient + consult actions (wraps to a new line under md) */}
                <div className="flex items-center gap-2 flex-1 md:flex-none basis-full md:basis-auto md:contents">
                  <select
                    value={a.status}
                    onChange={(e) => setStatusFor(a.id, e.target.value)}
                    className="input-clean text-xs flex-1 md:flex-none md:w-32 py-2 px-3"
                    data-testid={`appts-status-${a.id}`}
                  >
                    {STATUSES.filter((s) => s !== "all").map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                  <Link to={`/patients/${a.patient_id}`} className="btn-outline-emerald px-3 py-1.5 flex-none whitespace-nowrap text-xs sm:text-sm"><FileText size={14} /> <span className="hidden sm:inline">Patient</span></Link>
                  {a.role_in_appt === "invited" ? (
                    <Link
                      to={`/second-opinion/${a.id}`}
                      className="btn-emerald px-3 sm:px-4 py-1.5 flex-none whitespace-nowrap text-xs sm:text-sm"
                      data-testid={`appts-second-opinion-${a.id}`}
                    >
                      <CalendarClock size={14} /> <span>Second opinion</span>
                    </Link>
                  ) : (
                    <Link to={`/consultation/new?patient=${a.patient_id}&appt=${a.id}`} className="btn-emerald px-3 sm:px-4 py-1.5 flex-none whitespace-nowrap text-xs sm:text-sm"><CalendarClock size={14} /> Consult</Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <VideoCallDialog
        open={openCall}
        onOpenChange={setOpenCall}
        appointment={callAppt}
        doctorName={user?.name}
        onRoomChange={(room) => { setCallAppt((c) => c ? { ...c, meeting_room: room } : c); load(); }}
      />
    </Layout>
  );
}
